/**
 * Tactical Patterns Adapter
 * 
 * This adapter serves as the single source of truth for all tactical patterns.
 * It provides a consistent interface for accessing tactical patterns regardless of
 * whether they come from handcrafted examples or real-world examples from the
 * chess_tactics JSON files.
 * 
 * This consolidates all tactical pattern data structures following DRY and SOLID principles.
 */

import { TACTICAL_PATTERNS as originalPatterns, TacticalPattern, EnhancedTacticalPattern } from './tacticalThemePatterns';
import { TACTICAL_PATTERNS as generatedPatterns } from './generatedTacticalPatterns';
import { getAvailableTacticalThemes, getTacticalThemeDescription } from './tacticalThemePatterns';
import { getHintsForTheme } from '../../data/tacticalHints';

/**
 * Pattern metadata interface
 */
export interface PatternMetadata {
  totalPatterns: number;
  themes: string[];
  difficultyRange: [number, number];
  lastUpdated: string;
  sources: {
    original: number;
    generated: number;
  };
}

/**
 * Enhanced tactical patterns combining both original and generated patterns
 */
export const ENHANCED_TACTICAL_PATTERNS: Record<string, TacticalPattern[]> = {};

/**
 * Initialize the enhanced patterns
 * This function merges the generated patterns with the original ones
 */
function initializeEnhancedPatterns(): void {
  // Get all unique themes from both original and generated patterns
  const allThemes = new Set([
    ...Object.keys(originalPatterns),
    ...Object.keys(generatedPatterns)
  ]);

  // For each theme, combine patterns from both sources
  allThemes.forEach(tacticType => {
    const originalThemePatterns = originalPatterns[tacticType] || [];
    const generatedThemePatterns = generatedPatterns[tacticType] || [];
    
    // Combine patterns: generated first (higher priority), then original as fallback
    ENHANCED_TACTICAL_PATTERNS[tacticType] = [
      ...generatedThemePatterns,
      ...originalThemePatterns
    ];
  });
}

/**
 * Converts a TacticalPattern to an EnhancedTacticalPattern with hints and difficulty
 * @param pattern The base tactical pattern
 * @param theme The tactical theme for hint lookup
 * @returns Enhanced pattern with hints and difficulty
 */
function enhancePattern(pattern: TacticalPattern, theme: string): EnhancedTacticalPattern {
  const hints = getHintsForTheme(theme);
  
  // Calculate difficulty based on move count and complexity
  const difficulty = Math.min(5, Math.max(1, Math.ceil(pattern.moves.length / 2)));
  
  return {
    ...pattern,
    hints,
    difficulty,
    commonMistakes: generateCommonMistakes(theme)
  };
}

/**
 * Generates common mistakes for a tactical theme
 * @param theme The tactical theme
 * @returns Array of common mistakes
 */
function generateCommonMistakes(theme: string): string[] {
  const mistakes: Record<string, string[]> = {
    fork: [
      'Not checking if the forking piece is protected',
      'Missing that the opponent can capture the forking piece',
      'Forking pieces of equal or lesser value'
    ],
    pin: [
      'Not recognizing when a piece is pinned',
      'Moving a pinned piece and exposing a more valuable piece',
      'Missing opportunities to exploit pinned pieces'
    ],
    discoveredAttack: [
      'Not seeing the piece behind the moving piece',
      'Missing the discovered check opportunity',
      'Moving the wrong piece and missing the discovery'
    ],
    sacrifice: [
      'Sacrificing without sufficient compensation',
      'Not calculating the follow-up accurately',
      'Missing defensive resources for the opponent'
    ],
    checkmate: [
      'Not forcing the sequence to completion',
      'Allowing the opponent escape squares',
      'Missing the final mating move'
    ]
  };
  
  return mistakes[theme] || ['Not calculating all variations thoroughly'];
}

/**
 * Gets enhanced patterns for a specific theme
 * @param theme The tactical theme
 * @returns Array of enhanced tactical patterns
 */
export function getEnhancedPatternsForTheme(theme: string): EnhancedTacticalPattern[] {
  const patterns = ENHANCED_TACTICAL_PATTERNS[theme] || [];
  return patterns.map(pattern => enhancePattern(pattern, theme));
}

/**
 * Gets all enhanced patterns organized by theme
 * @returns Record of all enhanced tactical patterns
 */
export function getAllEnhancedPatterns(): Record<string, EnhancedTacticalPattern[]> {
  const enhanced: Record<string, EnhancedTacticalPattern[]> = {};
  
  Object.keys(ENHANCED_TACTICAL_PATTERNS).forEach(theme => {
    enhanced[theme] = getEnhancedPatternsForTheme(theme);
  });
  
  return enhanced;
}

/**
 * Gets a pattern by its unique identifier (theme + index)
 * @param id Pattern identifier in format "theme:index"
 * @returns Enhanced tactical pattern or undefined if not found
 */
export function getPatternById(id: string): EnhancedTacticalPattern | undefined {
  const [theme, indexStr] = id.split(':');
  const index = parseInt(indexStr, 10);
  
  if (!theme || isNaN(index)) {
    return undefined;
  }
  
  const patterns = getEnhancedPatternsForTheme(theme);
  return patterns[index];
}

/**
 * Gets metadata about the pattern database
 * @returns Pattern metadata
 */
export function getPatternMetadata(): PatternMetadata {
  const themes = getAvailableTacticalThemes();
  let totalPatterns = 0;
  let originalCount = 0;
  let generatedCount = 0;
  
  themes.forEach(theme => {
    const patterns = ENHANCED_TACTICAL_PATTERNS[theme] || [];
    totalPatterns += patterns.length;
    
    // Count original vs generated patterns
    const originalThemePatterns = originalPatterns[theme] || [];
    const generatedThemePatterns = generatedPatterns[theme] || [];
    
    originalCount += originalThemePatterns.length;
    generatedCount += generatedThemePatterns.length;
  });
  
  return {
    totalPatterns,
    themes,
    difficultyRange: [1, 5],
    lastUpdated: new Date().toISOString(),
    sources: {
      original: originalCount,
      generated: generatedCount
    }
  };
}

/**
 * Searches patterns by description or moves
 * @param query Search query
 * @returns Array of matching enhanced patterns with their theme
 */
export function searchPatterns(query: string): Array<{ theme: string; pattern: EnhancedTacticalPattern }> {
  const results: Array<{ theme: string; pattern: EnhancedTacticalPattern }> = [];
  const lowercaseQuery = query.toLowerCase();
  
  Object.keys(ENHANCED_TACTICAL_PATTERNS).forEach(theme => {
    const patterns = getEnhancedPatternsForTheme(theme);
    patterns.forEach(pattern => {
      if (
        pattern.description.toLowerCase().includes(lowercaseQuery) ||
        pattern.moves.some(move => move.toLowerCase().includes(lowercaseQuery)) ||
        theme.toLowerCase().includes(lowercaseQuery)
      ) {
        results.push({ theme, pattern });
      }
    });
  });
  
  return results;
}

// Initialize the enhanced patterns as soon as this module is imported
initializeEnhancedPatterns();

// Export the original functions for backward compatibility
export {
  getAvailableTacticalThemes,
  getTacticalThemeDescription
};

export type {
  TacticalPattern,
  EnhancedTacticalPattern
};

// Export the enhanced patterns as the main TACTICAL_PATTERNS export
export { ENHANCED_TACTICAL_PATTERNS as TACTICAL_PATTERNS };

export default {
  TACTICAL_PATTERNS: ENHANCED_TACTICAL_PATTERNS,
  getAvailableTacticalThemes,
  getTacticalThemeDescription,
  getEnhancedPatternsForTheme,
  getAllEnhancedPatterns,
  getPatternById,
  getPatternMetadata,
  searchPatterns
}; 